<template>
  <Dialog ref="dialog" class="" dialog-mode="small" content-class="px-4 pb-8 pt-5" header-class="">
    <template #title>
      <h4 class="m-0 text-xl">{{ $t("shared.account.address_validation_modal.title") }}</h4>
    </template>
    <template #content>
      <VcTypography class="leading-6">
        {{ $t("shared.account.address_validation_modal.description") }}
      </VcTypography>
      <div class="address_entry mt-5 flex flex-col">
        <span class="mb-3 font-bold">{{ $t("shared.account.address_validation_modal.your_entry") }}</span>
        <VcRadioButton
          id="address_entry"
          v-model="selectedAddress"
          value="userEntry"
          name="addressSelection"
          class="rounded-md border p-4"
        >
          <ul v-if="existingAddressEdit" class="flex flex-col gap-1 font-medium">
            <!-- Japan-specific address format -->
            <template v-if="existingAddressEdit.countryCode === 'JPN'">
              <li class="mb-2 font-semibold">{{ existingAddressEdit.lastName }} {{ existingAddressEdit.firstName }}</li>
              <li>{{ existingAddressEdit.postalCode }}</li>
              <li>{{ existingAddressEdit.regionName }}</li>
              <li>{{ existingAddressEdit.city }}</li>
              <li>{{ existingAddressEdit.line1 }}</li>
              <li v-if="existingAddressEdit.buildingTL">{{ existingAddressEdit.buildingTL }}</li>
            </template>
            <!-- Other formats -->
            <template v-else>
              <li v-if="existingAddressEdit.countryCode === 'CHN'" class="mb-2 font-semibold">
                {{ existingAddressEdit.lastName }} {{ existingAddressEdit.firstName }}
              </li>
              <li v-else class="mb-2 font-semibold">
                {{ existingAddressEdit.firstName }} {{ existingAddressEdit.lastName }}
              </li>
              <li v-if="existingAddressEdit.organization">{{ existingAddressEdit.organization }}</li>
              <li v-if="existingAddressEdit.departmentTL">{{ existingAddressEdit.departmentTL }}</li>
              <li v-if="existingAddressEdit.buildingTL || existingAddressEdit.mailStopTL">
                {{ existingAddressEdit.buildingTL ?? "" }} {{ existingAddressEdit.mailStopTL ?? "" }}
              </li>
              <li>{{ existingAddressEdit.line1 }}</li>
              <li v-if="isMUCCountry(existingAddressEdit.countryCode!)">
                {{ existingAddressEdit.regionName || existingAddressEdit.countyTL }}
                {{ existingAddressEdit.postalCode }} {{ existingAddressEdit.city }}
              </li>
              <li v-else>
                {{ existingAddressEdit.city }}, {{ existingAddressEdit.regionName || existingAddressEdit.countyTL }}
                {{ existingAddressEdit.postalCode }}
              </li>
              <li class="mb-3">{{ existingAddressEdit.countryName }}</li>
            </template>
          </ul>
        </VcRadioButton>
      </div>
      <div class="address_suggested-address mt-5 flex flex-col">
        <span class="mb-3 font-bold">{{ $t("shared.account.address_validation_modal.suggested_address") }}</span>
        <VcRadioButton
          id="suggested-address"
          v-model="selectedAddress"
          value="suggestedAddress"
          name="addressSelection"
          class="rounded-md border p-4"
        >
          <ul v-if="suggestedAddress" class="flex flex-col gap-1 font-medium">
            <!-- Japan-specific address format -->
            <template v-if="suggestedAddress.countryCode === 'JPN'">
              <li class="mb-2 font-semibold">{{ suggestedAddress.lastName }} {{ suggestedAddress.firstName }}</li>
              <li>{{ suggestedAddress.postalCode }}</li>
              <li>{{ suggestedAddress.regionName }}</li>
              <li>{{ suggestedAddress.city }}</li>
              <li>{{ suggestedAddress.line1 }}</li>
              <li v-if="suggestedAddress.buildingTL">{{ suggestedAddress.buildingTL }}</li>
            </template>
            <!-- Other formats -->
            <template v-else>
              <li v-if="suggestedAddress.countryCode === 'CHN'" class="mb-2 font-semibold">
                {{ suggestedAddress.lastName }} {{ suggestedAddress.firstName }}
              </li>
              <li v-else class="mb-2 font-semibold">
                {{ suggestedAddress.firstName }} {{ suggestedAddress.lastName }}
              </li>
              <li v-if="suggestedAddress.organization">{{ suggestedAddress.organization }}</li>
              <li v-if="suggestedAddress.departmentTL">{{ suggestedAddress.departmentTL }}</li>
              <li v-if="suggestedAddress.buildingTL || suggestedAddress.mailStopTL">
                {{ suggestedAddress.buildingTL ?? "" }} {{ suggestedAddress.mailStopTL ?? "" }}
              </li>
              <li>{{ suggestedAddress.line1 }}</li>
              <li v-if="isMUCCountry(suggestedAddress.countryCode!)">
                {{ suggestedAddress.regionName || suggestedAddress.countyTL }} {{ suggestedAddress.postalCode }}
                {{ suggestedAddress.city }}
              </li>
              <li v-else>
                {{ suggestedAddress.city }}, {{ suggestedAddress.regionName || suggestedAddress.countyTL }}
                {{ suggestedAddress.postalCode }}
              </li>
              <li class="mb-3">{{ suggestedAddress.countryName }}</li>
            </template>
          </ul>
        </VcRadioButton>
      </div>
      <div class="mt-5 flex w-full flex-col gap-5">
        <TlLoadingButton color="primary" size="lg" variant="solid" class="h-[48px]" @click="updateAddress">
          <span>{{ $t("shared.account.address_validation_modal.use_selected_address") }}</span>
        </TlLoadingButton>
        <VcButton color="secondary" size="lg" variant="outline" class="h-[48px]" @click="editExistingAddress">
          <span>{{ $t("shared.account.address_validation_modal.edit_address") }}</span>
        </VcButton>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import { AddressType } from "@/core/enums";
import { isMUCCountry } from "@/core/utilities/address";
import { useAddressModal } from "@/shared/account/composables/useAddressModal";
import { useUserAddresses } from "../composables";
import type { MemberAddressTypeExtension } from "@/core/api/graphql/types";
import Dialog from "@/shared/thorlabs/shared/components/dialog-modal.vue";
const emit = defineEmits<{
  (e: "updateAddress", address: MemberAddressTypeExtension): void;
}>();
const props = defineProps<IProps>();
const dialog = ref<InstanceType<typeof Dialog> | null>(null);

const showDialog = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  dialog.value?.openDialog();
};

interface IProps {
  existingAddressEdit: MemberAddressTypeExtension;
  suggestedAddress: MemberAddressTypeExtension;
  userTypedInAddress: MemberAddressTypeExtension;
  isEditAddressDisabled?: boolean;
}

const selectedAddress = ref<string>();
const { openAddOrUpdateAddressModal } = useAddressModal(showDialog);
const { addOrUpdateAddresses } = useUserAddresses();

const updateAddress = async (callback: (result: unknown) => void) => {
  try {
    if (selectedAddress.value === "userEntry") {
      await addOrUpdateAddresses([{ ...props.userTypedInAddress, addressType: AddressType.BillingAndShipping }]);
      dialog.value?.closeDialog();
      emit("updateAddress", props.userTypedInAddress);
    } else {
      await addOrUpdateAddresses([{ ...props.suggestedAddress, addressType: AddressType.BillingAndShipping }]);
      dialog.value?.closeDialog();
      emit("updateAddress", props.suggestedAddress);
    }
  } finally {
    callback(undefined);
  }
};

function editExistingAddress(): void {
  if (!props.isEditAddressDisabled) {
    openAddOrUpdateAddressModal(props.existingAddressEdit);
  }
  dialog.value?.closeDialog();
}

// Expose methods to parent components
defineExpose({
  showDialog,
});
</script>
